// src/pages/shop/ShopPrintCatalog.jsx
//
// Бумажный каталог: выбираем товары со сгенерированным штрихкодом (префикс 20),
// один тумблер режима (просто / по категориям), жмём — получаем PDF-журнал.

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { products as productsApi, categories as categoriesApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import Icon from '../../components/Icon';
import { isGeneratedBarcode, NONAME_BARCODE } from '../../utils/generateBarcode';
import { generateCatalogPDF } from '../../utils/generateCatalogPDF';
import { savePDF } from '../../utils/savePDF';

export default function ShopPrintCatalog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();

  const [all, setAll] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [byCategory, setByCategory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [prods, categories] = await Promise.all([
          productsApi.list(),
          categoriesApi.list().catch(() => []),
        ]);
        // только товары с нашим сгенерированным баркодом
        const own = (prods || []).filter(p => isGeneratedBarcode(p.barcode));
        setAll(own);
        setCats(categories || []);
        // по умолчанию выбираем все
        setSelected(new Set(own.map(p => p.id)));
      } catch (e) {
        show(t('common.errorLoad', 'Не удалось загрузить'), 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const catName = useMemo(() => {
    const m = new Map(cats.map(c => [c.id, c.name]));
    return (id) => m.get(id) || t('catalog.noCategory', 'Без категории');
  }, [cats, t]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter(p => (p.name || '').toLowerCase().includes(s));
  }, [all, q]);

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allChosen = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const toggleAll = () => setSelected(prev => {
    const n = new Set(prev);
    if (allChosen) filtered.forEach(p => n.delete(p.id));
    else filtered.forEach(p => n.add(p.id));
    return n;
  });

  const build = async () => {
    // Пустой выбор — тоже можно: напечатается одна карточка «Товар без названия»
    const chosen = all.filter(p => selected.has(p.id));
    setBusy(true);
    setProgress({ done: 0, total: chosen.length });
    try {
      const toItem = (p) => ({
        name: p.name,
        barcode: p.barcode,
        plu: p.plu != null ? p.plu : null,
        photo: (Array.isArray(p.photos) && p.photos[0]) || p.photo || null,
      });

      let groups;
      if (byCategory) {
        const map = new Map();
        for (const p of chosen) {
          const key = p.categoryId || '__none';
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(p);
        }
        groups = [...map.entries()].map(([key, arr]) => ({
          title: key === '__none' ? t('catalog.noCategory', 'Без категории') : catName(key),
          items: arr.map(toItem),
        }));
      } else {
        groups = [{ title: '', items: chosen.map(toItem) }];
      }
      // Первой карточкой — «ноунэйм»: для товара без кода и не из базы.
      // Касса по этому коду сразу просит цену.
      const noNameCard = { name: t('catalog.noNameCard'), barcode: NONAME_BARCODE, noName: true, noNameMark: '?' };
      if (groups.length === 0) groups = [{ title: '', items: [] }];
      groups[0].items.unshift(noNameCard);

      const blob = await generateCatalogPDF(
        groups,
        { mode: byCategory ? 'byCategory' : 'simple', cols: 3, rows: 6 },
        (done, total) => setProgress({ done, total }),
      );
      const stamp = new Date().toISOString().slice(0, 10);
      await savePDF(blob, `catalog-${stamp}.pdf`);
      show(t('catalog.ready', 'Каталог готов'), 'success');
    } catch (e) {
      show(t('catalog.buildError', 'Не удалось создать каталог'), 'error');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="page" style={{ paddingBottom: 100 }}>
      {ToastView}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}><Icon name="chevronLeft" size={18} /></button>
        <h1 className="page-title">{t('catalog.printTitle', 'Бумажный каталог')}</h1>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
      ) : all.length === 0 ? (
        <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--fg-muted)' }}>
          <Icon name="sparkles" size={32} />
          <p style={{ marginTop: 10, fontSize: 14 }}>
            {t('catalog.emptyHint', 'Пока нет товаров со сгенерированным штрихкодом. Создайте товар и нажмите «Создать» рядом с полем штрихкода.')}
          </p>
          <p style={{ marginTop: 10, fontSize: 13 }}>{t('catalog.noNameOnly')}</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 12 }}>
            {t('catalog.intro', 'В список попадают только товары со штрихкодом, созданным приложением.')}
          </p>

          {/* Режим */}
          <button
            type="button"
            onClick={() => setByCategory(v => !v)}
            className="card"
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 14px', marginBottom: 12, border: '1px solid var(--border)', textAlign: 'left' }}
          >
            <span style={{ width: 40, height: 24, borderRadius: 999, background: byCategory ? 'var(--brand-primary)' : 'var(--border-strong, #ccc)', position: 'relative', flexShrink: 0, transition: 'background .15s' }}>
              <span style={{ position: 'absolute', top: 2, left: byCategory ? 18 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{t('catalog.byCategory', 'Разбить по категориям')}</span>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-faint)' }}>
                {byCategory ? t('catalog.byCategoryOn', 'Каждая категория — со своей страницы') : t('catalog.byCategoryOff', 'Все товары подряд')}
              </span>
            </span>
          </button>

          {/* Поиск + выбрать все */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input className="input" placeholder={t('cashier.searchPlaceholder', 'Поиск')} value={q}
              onChange={(e) => setQ(e.target.value)} style={{ flex: 1, marginBottom: 0 }} />
            <button className="btn btn-secondary" onClick={toggleAll} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              {allChosen ? t('catalog.clearAll', 'Снять') : t('catalog.selectAll', 'Все')}
            </button>
          </div>

          <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginBottom: 10 }}>
            {t('catalog.chosenCount', 'Выбрано')}: {selected.size} / {all.length}
          </p>

          {/* Список */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* «Ноунэйм» — всегда первой карточкой, не снимается */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-subtle)', border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--fg-faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="check" size={13} stroke={3} color="#fff" />
              </span>
              <span style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--bg-elevated)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: 'var(--fg-muted)' }}>?</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{t('catalog.noNameCard')}</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--fg-faint)' }}>{t('catalog.noNameCardHint')}</span>
              </span>
            </div>
            {filtered.map(p => {
              const on = selected.has(p.id);
              const photo = (Array.isArray(p.photos) && p.photos[0]) || p.photo || null;
              return (
                <button key={p.id} onClick={() => toggle(p.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-elevated)', border: `1px solid ${on ? 'var(--brand-primary)' : 'var(--border)'}`, borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${on ? 'var(--brand-primary)' : 'var(--border-strong, #ccc)'}`, background: on ? 'var(--brand-primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {on && <Icon name="check" size={13} stroke={3} color="#fff" />}
                  </span>
                  {photo
                    ? <img src={photo} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                    : <span style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--bg-subtle)', flexShrink: 0 }} />}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>{p.barcode}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Нижняя кнопка */}
      {!loading && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 20px calc(12px + env(safe-area-inset-bottom))', background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-primary" onClick={build} disabled={busy} style={{ width: '100%' }}>
            {busy
              ? (progress ? t('catalog.building', 'Готовим… {{d}}/{{t}}', { d: progress.done, t: progress.total }) : '…')
              : t('catalog.make', 'Создать каталог ({{n}})', { n: selected.size + 1 })}

          </button>
        </div>
      )}
    </div>
  );
}
